import {
  assertInstance,
  isEmpty,
  notEmpty,
  parseError,
  parseJsonArrayAs,
  parseRecordAs,
  parseString,
  parsingArrayAs,
  substringAfter,
} from "@de-xima/xima-common-js-lang";

import { deferredToPromise, runRemoteCommand } from "../../Util.ts";
import { showInfoSavedNotification } from "../../elfinder/ElFinder.ts";

const RegExpFormTheme = /^l[a-zA-Z0-9]+_$/;

// biome-ignore lint/suspicious/noExplicitAny: TS has no existential types
type RemoteCommandParams<T> = T extends PrimeFaces.ajax.RemoteCommand<infer Params, any> ? Params : never;
// biome-ignore lint/suspicious/noExplicitAny: TS has no existential types
type RemoteCommandReturn<T> = T extends PrimeFaces.ajax.RemoteCommand<any, infer Return> ? Return : never;
type Property<T, K> = K extends keyof Exclude<T, undefined | null> ? Exclude<T, undefined | null>[K] : never;
type UpdateThemeDetailsParams = RemoteCommandParams<
  Property<Property<typeof BackendCommand, "ClientFormTheme">, "updateThemeDetail">
>;
type GetThemeDetailsReturn = RemoteCommandReturn<
  Property<Property<typeof BackendCommand, "ClientFormTheme">, "getThemeDetails">
>;

interface ThemeOption {
  readonly name: string;
  readonly value: string;
}

interface ThemeOptionGroup {
  readonly name: string;
  readonly options: readonly ThemeOption[];
}

interface ThemeDetails {
  readonly themeName: string;
  readonly themeUuid: string;
  readonly themeNames: readonly string[];
  readonly themeDescription: string;
  readonly themeParent: string;
  readonly themeParentOptions: readonly ThemeOptionGroup[];
}

function parseThemeOption(value: unknown): ThemeOption {
  return parseRecordAs(value, {
    name: parseString,
    value: parseString,
  });
}

function parseThemeOptionGroup(value: unknown): ThemeOptionGroup {
  return parseRecordAs(value, {
    name: parseString,
    options: parsingArrayAs(parseThemeOption),
  });
}

function parseThemeDetails(response: Partial<GetThemeDetailsReturn>): ThemeDetails {
  return {
    themeName: response.themeName ?? "",
    themeNames: parseJsonArrayAs(response.themeNames, parseString),
    themeUuid: parseString(response.themeUuid),
    themeDescription: response.themeDescription ?? "",
    themeParent: response.themeParent ?? "",
    themeParentOptions: parseJsonArrayAs(response.themeParentOptions, parseThemeOptionGroup),
  };
}

async function loadThemeDetails(volumeId: string): Promise<ThemeDetails> {
  const response = await runRemoteCommand(BackendCommand?.ClientFormTheme?.getThemeDetails, [
    { name: "volumeId", value: volumeId },
  ]);
  return parseThemeDetails(response);
}

async function updateThemeDetails(
  file: elFinder.ElFinderFile,
  fm: elFinder.ElFinderInstance,
  dialog: JQuery<HTMLElement>,
  newData: PrimeFaces.ajax.RemoteCommandParams<UpdateThemeDetailsParams>,
): Promise<boolean> {
  try {
    await runRemoteCommand(BackendCommand?.ClientFormTheme?.updateThemeDetail, [
      { name: "volumeId", value: file.volumeid ?? "" },
      ...newData,
    ]);
    const newName = newData.filter((x) => x.name === "themeName").map((x) => x.value)[0];
    if (notEmpty(newName)) {
      dialog.find(".elfinder-info-title strong").text(newName);
    }
    showInfoSavedNotification(fm, dialog);
    return true;
  } catch (e) {
    console.error(e);
    dialog.closest(".ui-dialog").find(".ui-dialog-titlebar-close").trigger("mousedown");
    fm.error(["errUpdateThemeDetail", parseError(e)]);
    await deferredToPromise(fm.sync());
    return false;
  }
}

function prepareUiThemeName(
  file: elFinder.ElFinderFile,
  fm: elFinder.ElFinderInstance,
  dialog: JQuery<HTMLElement>,
  themeDetails: ThemeDetails,
) {
  const existingNames = new Set(themeDetails.themeNames);
  let savedValue = themeDetails.themeName;

  const $spinner = dialog.find(".elfinder-info-name-spinner");
  const $input = dialog.find(".elfinder-info-name-input");
  const input = $input.get(0);
  assertInstance(input, HTMLInputElement);

  input.value = themeDetails.themeName;
  input.maxLength = 100;
  input.addEventListener("input", (e) => e.stopPropagation());
  input.addEventListener("blur", async (e) => {
    e.stopPropagation();
    const newName = input.value;
    if (isEmpty(newName)) {
      input.value = savedValue;
      return;
    }
    if (savedValue === newName) {
      return;
    }
    if (existingNames.has(newName) && newName !== themeDetails.themeName) {
      input.value = savedValue;
      return;
    }
    const elFile = fm.file(file.hash);
    if (elFile === undefined) {
      return;
    }
    const success = await updateThemeDetails(elFile, fm, dialog, [{ name: "themeName", value: newName }]);
    if (success) {
      savedValue = newName;
      elFile.name = newName;
      fm.updateCache({ changed: [elFile] });
      fm.change({ changed: [elFile] });
    }
  });
  input.addEventListener("keyup", (e) => e.stopPropagation());
  input.addEventListener("keydown", (e) => e.stopPropagation());

  $spinner.hide();
  $input.show();
}

function prepareUiThemeDescription(
  file: elFinder.ElFinderFile,
  fm: elFinder.ElFinderInstance,
  dialog: JQuery<HTMLElement>,
  themeDetails: ThemeDetails,
) {
  const $spinner = dialog.find(".elfinder-info-desc-spinner");
  const $textarea = dialog.find(".elfinder-info-desc-textarea");
  const textarea = $textarea.get(0);
  let savedValue = themeDetails.themeDescription;
  assertInstance(textarea, HTMLTextAreaElement);

  textarea.value = themeDetails.themeDescription;
  textarea.addEventListener("input", (e) => e.stopPropagation());
  textarea.addEventListener("blur", async (e) => {
    e.stopPropagation();
    const newDescription = textarea.value;
    if (savedValue === newDescription) {
      return;
    }
    await updateThemeDetails(file, fm, dialog, [{ name: "themeDescription", value: textarea.value }]);
    savedValue = newDescription;
  });
  textarea.addEventListener("keyup", (e) => e.stopPropagation());
  textarea.addEventListener("keydown", (e) => e.stopPropagation());
  textarea.maxLength = 1000;

  $spinner.hide();
  $textarea.show();
}

function prepareUiThemeUuid(
  _file: elFinder.ElFinderFile,
  _fm: elFinder.ElFinderInstance,
  dialog: JQuery<HTMLElement>,
  themeDetails: ThemeDetails,
) {
  const $spinner = dialog.find(".elfinder-info-theme-uuid-spinner");
  $spinner.text(themeDetails.themeUuid);
  $spinner.removeClass("elfinder-info-spinner");
}

function prepareUiThemeParentTheme(
  file: elFinder.ElFinderFile,
  fm: elFinder.ElFinderInstance,
  dialog: JQuery<HTMLElement>,
  themeDetails: ThemeDetails,
) {
  const $spinner = dialog.find(".elfinder-info-parent-spinner");
  const $select = dialog.find(".elfinder-info-parent-select");

  const select = $select.get(0);
  assertInstance(select, HTMLSelectElement);

  for (const group of themeDetails.themeParentOptions) {
    const optGroup = document.createElement("optgroup");
    optGroup.label = group.name;
    select.appendChild(optGroup);
    for (const option of group.options) {
      const optionElement = document.createElement("option");
      optionElement.value = option.value;
      optionElement.text = option.name;
      if (option.value === themeDetails.themeParent) {
        optionElement.selected = true;
      }
      optGroup.appendChild(optionElement);
    }
  }

  select.addEventListener("change", async (e) => {
    e.stopPropagation();
    await updateThemeDetails(file, fm, dialog, [{ name: "themeParent", value: select.value }]);
  });

  $spinner.hide();
  $select.show();
}

export const InfoItemThemeName: elFinder.ElFinderCustomInfoItem = {
  hashRegex: RegExpFormTheme,
  label: "infoItemThemeName",
  tpl: [
    `<div class="elfinder-info-name">`,
    `<span class="elfinder-info-spinner elfinder-info-name-spinner"></span>`,
    `<input class="elfinder-info-input elfinder-info-name-input" style="display:none;"></input>`,
    "</div>",
  ].join(""),
  action: async (file, fm, dialog) => {
    const themeDetails = await loadThemeDetails(file.volumeid ?? "");
    prepareUiThemeName(file, fm, dialog, themeDetails);
    prepareUiThemeDescription(file, fm, dialog, themeDetails);
    prepareUiThemeParentTheme(file, fm, dialog, themeDetails);
    prepareUiThemeUuid(file, fm, dialog, themeDetails);
    const dialogContainer = dialog.closest(".ui-dialog");

    dialogContainer.height(`${dialogContainer.height()}px`);

    const textarea = dialog.find(".elfinder-info-desc-textarea")[0];
    if (textarea !== undefined) {
      autosize(textarea);
    }
  },
};

export const InfoItemThemeDescription: elFinder.ElFinderCustomInfoItem = {
  hashRegex: RegExpFormTheme,
  label: "infoItemThemeDescription",
  tpl: [
    `<div class="elfinder-info-desc">`,
    `<span class="elfinder-info-spinner elfinder-info-desc-spinner"></span>`,
    `<textarea rows="1" class="elfinder-info-input elfinder-info-desc-textarea" style="display:none;"></textarea>`,
    "</div>",
  ].join(""),
  action: async () => {},
};

export const InfoItemParentTheme: elFinder.ElFinderCustomInfoItem = {
  hashRegex: RegExpFormTheme,
  label: "infoItemThemeParent",
  tpl: [
    `<div class="elfinder-info-parent">`,
    `<span class="elfinder-info-spinner elfinder-info-parent-spinner"></span>`,
    `<select class="elfinder-info-input elfinder-info-parent-select" style="display:none;"></select>`,
    "</div>",
  ].join(""),
  action: async () => {},
};

export const InfoItemThemeUuid: elFinder.ElFinderCustomInfoItem = {
  hashRegex: RegExpFormTheme,
  label: "infoItemThemeUuid",
  tpl: [
    `<div class="elfinder-info-parent">`,
    `<span class="elfinder-info-spinner elfinder-info-theme-uuid-spinner"></span>`,
    "</div>",
  ].join(""),
  action: async () => {},
};

export const InfoItemRelativeThemeFilePath: elFinder.ElFinderCustomInfoItem = {
  label: "infoItemRelativeThemeFilePath",
  tpl: [
    `<div class="elfinder-info-relative-theme-file-path">`,
    `<span class="elfinder-info-relative-theme-file-path-text"></span>`,
    "</div>",
  ].join(""),
  action: async (file, fm, dialog) => {
    const filePath = fm.path(file.hash, false);
    const pathTr = dialog.find(".elfinder-info-path").closest("tr");
    pathTr.hide();
    const itemTr = dialog.find(".elfinder-info-relative-theme-file-path").closest("tr");
    if (isEmpty(filePath) || file.mime === "directory") {
      itemTr.hide();
      return;
    }
    const relativePath = `../${substringAfter(filePath, "/")}`;
    dialog.find(".elfinder-info-relative-theme-file-path-text").text(relativePath);
    pathTr.closest("tr").after(itemTr);
  },
};
